import { Injectable } from '@angular/core';
import { filter, map, Observable, switchMap, take } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Credential, LocalCanal } from '@models/.';
import { environment } from '@environments/environment';
import { ResponseSuccess } from '@interfaces/response-success';
import { AuthenticationState } from '@store/reducers/authentication.reducer';
import { selectCredential } from '@store/selectors/authentication.selectors';
import { selectStoreChannelList } from '@store/selectors';
import { transformDropdown } from '@core/helpers';
import { SelectMenuItem } from '@core/interfaces';
import { Platform } from '@core/enums';

@Injectable({
  providedIn: 'root',
})
export class StoreChannelService {
  credential$: Observable<Credential | null>;
  localChannels$: Observable<LocalCanal[] | null>;

  constructor(
    private http: HttpClient,
    private store: Store<AuthenticationState>
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.localChannels$ = this.store.select(selectStoreChannelList);
  }

  find(id: string): Observable<ResponseSuccess<LocalCanal>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<LocalCanal>>(
          `${environment.microEnterprise}/api/v1/channel/${id}`,
          { headers: { Authorization: token } },
        );
      }),
      take(1),
    );
  }

  findByName(name: string): Observable<LocalCanal|undefined> {
    return this.localChannels$.pipe(
      filter((localChannels): localChannels is LocalCanal[] => localChannels !== null && localChannels.length > 0),
      map((localChannels) => localChannels.find((localChannel) => localChannel.name === name)),
      take(1),
    );
  }

  findAllByName(name?: string): Observable<LocalCanal[]> {
    return this.localChannels$.pipe(
      filter((localChannels): localChannels is LocalCanal[] => localChannels !== null && localChannels.length > 0),
      map((localChannels) => {
        return localChannels.filter((localChannel) => localChannel.name === name || !name);
      }),
    );
  }

  findAll(section?: string): Observable<ResponseSuccess<LocalCanal>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        const params = {
          ...(!!section && { seccion: section }),
        };

        return this.http.get<ResponseSuccess<LocalCanal>>(
          `${environment.microEnterprise}/api/v1/channel`,
          {
            headers: { Authorization: token },
            params: {
              ...params,
              // cuentaId: credential!.idCuenta!
            }
          },
        );
      }),
      take(1),
    );
  }

  findAllOrdered(): Observable<LocalCanal[]> {
    const customOrderChannels = [
      Platform.JuvoPos,
      Platform.JuvoOlo,
      Platform.JuvoKiosk,
    ];

    return this.localChannels$.pipe(
      filter((localChannels): localChannels is LocalCanal[] => localChannels !== null),
      map((channels) => (channels ?? []).map((channel) => channel).sort((a, b) => {
        const indexA = customOrderChannels.indexOf(a.name as Platform);
        const indexB = customOrderChannels.indexOf(b.name as Platform);
        
        if (indexA !== -1 && indexB !== -1)
          return indexA - indexB;
        
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        
        return 0;
      })),
    );
  }

  getDropdown(): Observable<SelectMenuItem<undefined>[]> {
    return this.localChannels$.pipe(
      filter((localChannels): localChannels is LocalCanal[] => Array.isArray(localChannels) && localChannels.length > 0),
      map((localChannels) => transformDropdown(localChannels, 'idChannel', 'name') as SelectMenuItem<undefined>[]),
      take(1),
    );
  }
}
